import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, ShieldCheck, CheckCircle2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { DropOffBlock, itemNeedsDropOff } from "@/components/DropOffChecklist";
import { trackPixelEvent } from "@/components/MetaPixel";
import { createOrder, money } from "@/lib/api";

const inputCls =
  "w-full border border-white/15 bg-ink px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-brand-cyan";
const errCls = "mt-1 font-mono text-[10px] uppercase tracking-wider text-brand-magenta";

const Checkout = () => {
  const { items, subtotal, discount, net, lineDiscount, clear, rushTotal } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    address: "", city: "", state: "", zip: "",
    method: "pickup",
    acceptedTerms: false,
  });
  const [errors, setErrors] = useState({});
  const [placing, setPlacing] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [payMethod, setPayMethod] = useState("wipay");
  const [payPlan, setPayPlan] = useState("full");

  // Meta Pixel: fire Purchase on the final checkout confirmation screen
  useEffect(() => {
    if (confirmation) {
      trackPixelEvent("Purchase", {
        value: confirmation.total,
        currency: "JMD",
        order_id: confirmation.order_id,
      });
    }
  }, [confirmation]);

  const shippingCost =
    form.method === "pickup" ? 0
      : form.method === "kingston" ? 1000
        : form.method === "portmore" ? 1500
          : form.method === "spanish-town" ? 2000
            : form.method === "knutsford" ? 1000
              : 0;
  const total = net + shippingCost;
  // Bulk orders (JMD $20,000+) may start with a 50% deposit; everything else is pay-in-full.
  const isBulk = total >= 20000;
  const effectivePlan = isBulk ? payPlan : "full";
  const charge = effectivePlan === "deposit" ? Math.round((total / 2) * 100) / 100 : total;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Valid email required";
    if (form.method !== "pickup") {
      if (!form.address.trim()) e.address = "Required";
      if (!form.city.trim()) e.city = "Required";
      if (!form.zip.trim()) e.zip = "Required";
    }
    if (!form.acceptedTerms) e.terms = "Please accept the Terms & Cancellation Policy";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Check the highlighted fields");
      return;
    }
    setPlacing(true);
    try {
      const payload = {
        customer: { name: form.name, email: form.email, phone: form.phone },
        shipping: {
          address: form.address, city: form.city, state: form.state, zip: form.zip,
          method: form.method, cost: shippingCost,
        },
        items: items.map((i) => ({
          slug: i.slug, name: i.name, qty: i.qty, unit_price: i.unitPrice,
          config: i.configLabel || null, artwork: i.artworkName || null,
          rush_fee: i.rushFee || 0, rush_label: i.rushLabel || null,
        })),
        payment: { provider: payMethod, currency: "JMD" },
        payment_method: payMethod,
        payment_plan: effectivePlan,
        subtotal: Number(subtotal.toFixed(2)),
        discount: Number(discount.toFixed(2)),
        shipping_cost: shippingCost,
        total: Number(total.toFixed(2)),
      };
      const res = await createOrder(payload);
      if (res.payment_url) {
        toast.success("Opening WiPay secure checkout…", { description: res.order_id });
        window.location.assign(res.payment_url);
        return;
      }
      setConfirmation({
        ...res,
        dropOff: items.some(itemNeedsDropOff),
        dropOffSneaker: items.some((i) => i.categorySlug === "customized-sneakers"),
      });
      clear();
    } catch {
      toast.error("Order failed — try again");
    } finally {
      setPlacing(false);
    }
  };

  const summary = useMemo(
    () => (
      <div className="border border-white/10 bg-ink-2">
        <div className="h-1 cmyk-line" />
        <div className="p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Order Summary</p>
          <ul className="mt-4 space-y-3">
            {items.map((i) => {
              const pct = lineDiscount(i);
              const gross = i.lineTotal != null ? i.lineTotal : i.unitPrice * i.qty;
              const discountBase =
                i.discountBase != null ? i.discountBase : i.discountUnit != null ? i.discountUnit * i.qty : gross;
              return (
                <li key={i.key} className="flex justify-between gap-3 text-sm">
                  <div>
                    <p className="font-display font-bold uppercase text-white">{i.name}</p>
                    {i.configLabel && (
                      <p className="font-mono text-[10px] uppercase text-brand-cyan">{i.configLabel}</p>
                    )}
                    <p className="font-mono text-[10px] text-zinc-500">qty {i.qty}</p>
                    {pct > 0 && (
                      <p
                        data-testid={`checkout-item-discount-${i.key}`}
                        className="mt-1 inline-block border border-brand-cyan/40 bg-brand-cyan/10 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-brand-cyan"
                      >
                        Bulk pricing applied
                      </p>
                    )}
                  </div>
                  <span className="font-mono text-sm text-zinc-300">
                    {pct > 0 && (
                      <span className="mr-2 text-zinc-500 line-through">{money(gross)}</span>
                    )}
                    {money(gross - (discountBase * pct) / 100 + (i.rushFee || 0))}
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="mt-5 space-y-2 border-t border-white/10 pt-4 font-mono text-sm">
            <div className="flex justify-between text-zinc-400">
              <span>Subtotal</span>
              <span data-testid="checkout-subtotal">{money(subtotal)}</span>
            </div>
            {rushTotal > 0 && (
              <div className="flex justify-between text-brand-yellow">
                <span data-testid="checkout-rush-label">Rush Order Upgrades (included)</span>
                <span data-testid="checkout-rush">{money(rushTotal)}</span>
              </div>
            )}
            {discount > 0 && (
              <div className="flex justify-between text-brand-cyan">
                <span data-testid="checkout-discount-label">You Save — Bulk Discount</span>
                <span data-testid="checkout-discount">{money(discount)}</span>
              </div>
            )}
            <div className="flex justify-between text-zinc-400">
              <span>Delivery / Pickup</span>
              <span data-testid="checkout-shipping">{shippingCost === 0 ? "FREE" : money(shippingCost)}</span>
            </div>
            <div className="flex justify-between border-t border-white/10 pt-3 text-base text-white">
              <span className="font-display font-bold uppercase">Total</span>
              <span data-testid="checkout-total" className="font-display text-2xl font-black text-brand-yellow">
                {money(total)}
              </span>
            </div>
          </div>
        </div>
      </div>
    ),
    [items, subtotal, discount, shippingCost, total, lineDiscount, rushTotal]
  );

  if (confirmation) {
    const isCash = confirmation.payment_method === "cash";
    const isDeposit = confirmation.payment_plan === "deposit";
    const dueNow = isDeposit ? confirmation.deposit_amount : confirmation.total;
    return (
      <div className="mx-auto max-w-2xl px-4 pb-32 pt-48 text-center sm:px-6" data-testid="checkout-success">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <CheckCircle2 className="mx-auto h-16 w-16 text-brand-cyan" />
          <h1 className="mt-6 font-display text-5xl font-black uppercase tracking-tight text-white">
            {isCash ? "Order Reserved" : "Order Locked In"}
          </h1>
          <p className="mt-3 font-mono text-sm uppercase tracking-[0.3em] text-brand-yellow">
            {confirmation.order_id}
          </p>
          {isCash ? (
            <div data-testid="checkout-cash-confirmation" className="mx-auto mt-5 max-w-md text-left">
              <p className="border border-brand-magenta/50 bg-brand-magenta/10 px-4 py-2 text-center font-mono text-xs font-bold uppercase tracking-widest text-brand-magenta">
                Awaiting Payment — {money(dueNow)} due
              </p>
              {isDeposit && (
                <div className="mt-4 space-y-1.5 border border-white/10 bg-ink-2 px-4 py-3 font-mono text-xs">
                  <div className="flex justify-between text-zinc-400"><span>Order Total</span><span>{money(confirmation.total)}</span></div>
                  <div className="flex justify-between text-brand-yellow"><span>Deposit Required</span><span>{money(confirmation.deposit_amount)}</span></div>
                  <div className="flex justify-between text-zinc-400"><span>Deposit Paid</span><span>{money(0)}</span></div>
                  <div className="flex justify-between text-brand-magenta"><span>Balance Due Before Collection</span><span>{money(confirmation.balance_due - confirmation.deposit_amount)}</span></div>
                </div>
              )}
              <p className="mt-4 text-sm leading-relaxed text-zinc-300">
                Production will <b>not</b> begin until your cash payment has been received and
                confirmed. Cash payment does <b>not</b> mean Cash on Delivery.
              </p>
              <p className="mt-2 font-mono text-[10px] uppercase leading-relaxed tracking-wider text-zinc-500">
                Unpaid cash orders expire 48 hours after placement. We've emailed you these
                instructions — bring payment to 24 Upper Elleston Road, Kingston 16.
              </p>
            </div>
          ) : (
            <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-zinc-400">
              Your order totalling {money(confirmation.total)} is in our queue. Our team will reach
              out to arrange payment, and a digital proof lands in your inbox before we print.
            </p>
          )}
          {confirmation.dropOff && (
            <div className="mx-auto mt-6 max-w-md">
              <DropOffBlock sneaker={confirmation.dropOffSneaker} />
            </div>
          )}
          <div className="mt-8 flex justify-center gap-4">
            <button
              data-testid="checkout-track-order"
              onClick={() => navigate("/track")}
              className="border border-brand-cyan/60 px-8 py-4 font-display text-base font-black uppercase tracking-widest text-brand-cyan transition-colors hover:bg-brand-cyan hover:text-ink"
            >
              Track Order
            </button>
            <button
              data-testid="checkout-continue-shopping"
              onClick={() => navigate("/")}
              className="bg-brand-yellow px-8 py-4 font-display text-base font-black uppercase tracking-widest text-ink transition-colors hover:bg-brand-cyan"
            >
              Keep Shopping
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-6 pb-32 pt-48 text-center" data-testid="checkout-empty">
        <p className="font-display text-4xl font-black uppercase text-zinc-500">Your cart is empty</p>
        <Link
          to="/"
          className="mt-6 inline-block bg-brand-yellow px-8 py-4 font-display text-base font-black uppercase tracking-widest text-ink hover:bg-brand-cyan"
        >
          Browse the Catalog
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="mx-auto max-w-7xl px-4 pb-24 pt-40 sm:px-6">
      <p className="font-mono text-xs uppercase tracking-[0.4em] text-brand-cyan">Secure Checkout</p>
      <h1 className="mt-2 font-display text-4xl font-black uppercase tracking-tight text-white sm:text-6xl">
        Final Pass
      </h1>

      <form onSubmit={submit} className="mt-10 grid gap-10 lg:grid-cols-[1.15fr_0.85fr]">
        <div className="space-y-8">
          <section className="border border-white/10 bg-ink-2 p-6">
            <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              01 — Contact
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <input data-testid="checkout-name" value={form.name} onChange={set("name")} placeholder="Full name *" className={inputCls} />
                {errors.name && <p className={errCls}>{errors.name}</p>}
              </div>
              <div>
                <input data-testid="checkout-email" value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} />
                {errors.email && <p className={errCls}>{errors.email}</p>}
              </div>
              <input data-testid="checkout-phone" value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" className={`${inputCls} sm:col-span-2`} />
            </div>
          </section>

          <section className="border border-white/10 bg-ink-2 p-6">
            <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              02 — Delivery / Pickup
            </p>
            <p className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-zinc-300">
              Choose Order Type
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                { id: "pickup", label: "Local Pickup (Kingston)" },
                { id: "delivery", label: "Delivery / Shipping" },
              ].map((t) => {
                const active = t.id === "pickup" ? form.method === "pickup" : form.method !== "pickup";
                return (
                  <button
                    type="button"
                    key={t.id}
                    data-testid={`checkout-order-type-${t.id}`}
                    onClick={() =>
                      setForm((f) => ({ ...f, method: t.id === "pickup" ? "pickup" : "kingston" }))
                    }
                    className={`border px-4 py-3.5 text-left font-display text-base font-bold uppercase tracking-wide transition-colors ${
                      active ? "border-brand-yellow bg-brand-yellow/10 text-brand-yellow" : "border-white/15 text-white hover:border-white/40"
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>

            {form.method === "pickup" ? (
              <div
                data-testid="checkout-pickup-badge"
                className="mt-4 flex items-center gap-3 border border-brand-cyan/40 bg-brand-cyan/10 px-4 py-3.5"
              >
                <span className="h-2.5 w-2.5 shrink-0 rotate-45 bg-brand-cyan" />
                <p className="font-mono text-xs leading-relaxed text-zinc-200">
                  Pickup Location:{" "}
                  <span className="font-bold text-brand-cyan">24 Upper Elleston Road, Kingston 16</span>{" "}
                  (FREE)
                </p>
              </div>
            ) : (
              <>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <input data-testid="checkout-address" value={form.address} onChange={set("address")} placeholder="Street address *" className={inputCls} />
                    {errors.address && <p className={errCls}>{errors.address}</p>}
                  </div>
                  <div>
                    <input data-testid="checkout-city" value={form.city} onChange={set("city")} placeholder="City *" className={inputCls} />
                    {errors.city && <p className={errCls}>{errors.city}</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <input data-testid="checkout-state" value={form.state} onChange={set("state")} placeholder="State" className={inputCls} />
                    <div>
                      <input data-testid="checkout-zip" value={form.zip} onChange={set("zip")} placeholder="ZIP *" className={inputCls} />
                      {errors.zip && <p className={errCls}>{errors.zip}</p>}
                    </div>
                  </div>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {[
                    { id: "kingston", label: "Kingston", detail: money(1000) },
                    { id: "portmore", label: "Portmore", detail: money(1500) },
                    { id: "spanish-town", label: "Spanish Town", detail: money(2000) },
                    { id: "knutsford", label: "Knutsford Express Drop-Off", detail: money(1000) },
                  ].map((m) => (
                    <button
                      type="button"
                      key={m.id}
                      data-testid={`checkout-shipping-${m.id}`}
                      onClick={() => setForm((f) => ({ ...f, method: m.id }))}
                      className={`border px-4 py-3 text-left transition-colors ${
                        form.method === m.id ? "border-brand-cyan bg-brand-cyan/10" : "border-white/15 hover:border-white/40"
                      }`}
                    >
                      <span className="block font-display text-base font-bold uppercase leading-tight text-white">{m.label}</span>
                      <span className="mt-1 block font-mono text-[10px] text-zinc-500">{m.detail}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </section>

          <section className="border border-white/10 bg-ink-2 p-6">
            <div className="mb-4 flex items-center justify-between">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                03 — Payment
              </p>
              <span data-testid="checkout-wipay-badge" className="flex items-center gap-1.5 border border-brand-cyan/40 px-2 py-1 font-mono text-[9px] uppercase tracking-widest text-brand-cyan">
                <ShieldCheck className="h-3 w-3" /> WiPay Secure • Jamaica
              </span>
            </div>

            {isBulk && (
              <div className="mb-5">
                <p className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-zinc-300">
                  Payment Plan
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    { id: "full", label: "Pay in Full", detail: `${money(total)} today` },
                    { id: "deposit", label: "Pay 50% Deposit", detail: `${money(charge)} today — balance before collection` },
                  ].map((p) => (
                    <button
                      type="button"
                      key={p.id}
                      data-testid={`checkout-plan-${p.id}`}
                      onClick={() => setPayPlan(p.id)}
                      className={`border px-4 py-3 text-left transition-colors ${
                        payPlan === p.id ? "border-brand-yellow bg-brand-yellow/10" : "border-white/15 hover:border-white/40"
                      }`}
                    >
                      <span className="block font-display text-base font-bold uppercase leading-tight text-white">{p.label}</span>
                      <span className="mt-1 block font-mono text-[10px] text-zinc-500">{p.detail}</span>
                    </button>
                  ))}
                </div>
                {effectivePlan === "deposit" && (
                  <div data-testid="checkout-deposit-notice" className="mt-4 border border-brand-yellow/40 bg-brand-yellow/5 px-4 py-4">
                    <div className="space-y-1.5 font-mono text-xs">
                      <div className="flex justify-between text-zinc-400">
                        <span>Order Total</span>
                        <span data-testid="checkout-deposit-order-total">{money(total)}</span>
                      </div>
                      <div className="flex justify-between text-brand-yellow">
                        <span>Deposit Required (50%)</span>
                        <span data-testid="checkout-deposit-required">{money(charge)}</span>
                      </div>
                      <div className="flex justify-between text-zinc-400">
                        <span>Balance Outstanding</span>
                        <span data-testid="checkout-deposit-balance">{money(total - charge)}</span>
                      </div>
                    </div>
                    <p className="mt-3 text-xs leading-relaxed text-zinc-300">
                      Bulk orders of J$20,000 or more may be started with a 50% deposit. Deposits
                      become non-refundable once artwork has been approved, materials have been
                      purchased, or production has begun. The remaining balance must be paid in
                      full before collection or delivery.
                    </p>
                  </div>
                )}
              </div>
            )}

            <p className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-zinc-300">
              Payment Method
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                data-testid="checkout-method-wipay"
                onClick={() => setPayMethod("wipay")}
                className={`border px-4 py-3 text-left transition-colors ${
                  payMethod === "wipay" ? "border-brand-cyan bg-brand-cyan/10" : "border-white/15 hover:border-white/40"
                }`}
              >
                <span className="block font-display text-base font-bold uppercase leading-tight text-white">WiPay — Pay Online</span>
                <span className="mt-1 block font-mono text-[10px] text-zinc-500">Visa & Mastercard, charged in JMD</span>
              </button>
              <button
                type="button"
                data-testid="checkout-method-cash"
                onClick={() => setPayMethod("cash")}
                className={`border px-4 py-3 text-left transition-colors ${
                  payMethod === "cash" ? "border-brand-cyan bg-brand-cyan/10" : "border-white/15 hover:border-white/40"
                }`}
              >
                <span className="block font-display text-base font-bold uppercase leading-tight text-white">Cash — Pay Before Production</span>
                <span className="mt-1 block font-mono text-[10px] text-zinc-500">Order reserved; production starts after payment</span>
              </button>
            </div>

            {payMethod === "wipay" ? (
              <div className="mt-4 flex items-start gap-3 border border-brand-cyan/30 bg-brand-cyan/5 px-4 py-4">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand-cyan" />
                <p className="text-sm leading-relaxed text-zinc-300">
                  Card details are entered on WiPay's encrypted hosted page — nothing card-related
                  ever touches our servers. Visa & Mastercard accepted, charged in JMD. You'll be
                  redirected to WiPay to complete payment, then returned here automatically.
                </p>
              </div>
            ) : (
              <div data-testid="checkout-cash-notice" className="mt-4 border border-brand-magenta/40 bg-brand-magenta/5 px-4 py-4">
                <p className="text-sm leading-relaxed text-zinc-200">
                  Cash payments must be made before production begins. Selecting Cash does not mean
                  Cash on Delivery. Your order will remain on hold until your payment has been
                  received and confirmed.
                </p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                  Unpaid cash orders expire 48 hours after placement.
                </p>
              </div>
            )}
          </section>
        </div>

        <div>
          <div className="sticky top-32 space-y-4">
            {summary}
            <label
              data-testid="checkout-terms-label"
              className={`flex cursor-pointer items-start gap-3 border px-4 py-3.5 transition-colors ${
                form.acceptedTerms
                  ? "border-brand-yellow/60 bg-brand-yellow/5"
                  : "border-white/15 hover:border-white/40"
              }`}
            >
              <input
                type="checkbox"
                data-testid="checkout-terms-checkbox"
                checked={form.acceptedTerms}
                onChange={(e) => setForm((f) => ({ ...f, acceptedTerms: e.target.checked }))}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[#FFE600]"
              />
              <span className="text-xs leading-relaxed text-zinc-300">
                I accept the{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="checkout-terms-link"
                  className="font-bold text-brand-yellow underline underline-offset-2 transition-colors hover:text-brand-cyan"
                >
                  Terms & Cancellation Policy
                </a>{" "}
                — all sales are final once my proof is approved and payment is submitted.
              </span>
            </label>
            {errors.terms && (
              <p data-testid="checkout-terms-error" className={errCls}>{errors.terms}</p>
            )}
            <button
              data-testid="checkout-form-submit"
              type="submit"
              disabled={placing}
              className="flex w-full items-center justify-center gap-2 bg-brand-yellow py-4 font-display text-lg font-black uppercase tracking-widest text-ink transition-colors hover:bg-brand-magenta hover:text-white disabled:opacity-50"
            >
              <Lock className="h-4 w-4" />
              {placing
                ? payMethod === "wipay" ? "Opening WiPay…" : "Placing Order…"
                : payMethod === "wipay"
                  ? `Pay ${money(charge)} via WiPay`
                  : `Place Order — ${money(charge)} Due in Cash`}
            </button>
            <p className="text-center font-mono text-[9px] uppercase tracking-widest text-zinc-600">
              256-bit encrypted • Processed by WiPay Jamaica in JMD
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Checkout;
